import { get, set } from '@ember/object';
import { RI_BREAKDOWNS } from 'frontend-utils/utils/constants/constant-values';
import { parseObject } from 'lumen/utils/parse-utils';

/**
 * Checks if a filter is already present
 * @param  { string }  segmentationId      : id of the block
 * @param  { string }  segmentationValueId : id of the value
 * @param  { object }  currentFilters      : current active filters
 * @return { boolean }                     : whether or not it has the fiter
 */
export function hasFilter(segmentationId, segmentationValueId, currentFilters) {
  if (currentFilters[segmentationId]) {
    return currentFilters[segmentationId].indexOf(segmentationValueId) !== -1;
  }

  return false;
}

/**
 * Adds a filter to the list of filters
 * Adds the key in the filters object if not already present
 * @param  { string }  segmentationId      : id of the block
 * @param  { string }  segmentationValueId : id of the value
 * @param  { object }  currentFilters      : current active filters
 * @return { object }                      : updated filters
 */
export function addFilter(segmentationId, segmentationValueId, currentFilters) {
  // If filter bucket not yet present, make it
  if (!currentFilters[segmentationId]) {
    currentFilters[segmentationId] = [];
  }

  currentFilters[segmentationId].push(segmentationValueId);

  return currentFilters;
}

/**
 * Adds multiple filters to the list of filters
 * @param { array } segmentationValueObjects : An array of segmentation value objects (note: these should be the originals from the APi)
 * @param { object }  currentFilters         : current active filters
 */
export function addFilters(segmentationValueObjects, currentFilters) {
  segmentationValueObjects.forEach((segmentation) => {
    addFilter(
      segmentation.segmentationId,
      segmentation.segmentationValueId,
      currentFilters,
    );
  });
}

/**
 * find the segmentation value object in the currently available blocks
 * Also loop over childeren (recursive)
 * @param  { array } segmentationValueObjectList : list of values for a certain block
 * @param  { string } searchValue                : the value that should match the filterproperty exactly
 * @param  { string } filterProperty             : the property of the segmentation value object that should match the searchvalue
 * @return { object }                            : the segmentation value object or null
 */
export function findSegmentationValueObject(
  segmentationValueObjectList,
  searchValue,
  filterProperty,
) {
  let res = null;
  segmentationValueObjectList.some((segmentationValueObject) => {
    // Check the object for matches
    if (get(segmentationValueObject, filterProperty) === searchValue) {
      res = segmentationValueObject;
      return true;
    }
    // Check the children for matches
    if (segmentationValueObject.children) {
      const foundSegmentationValueObject = findSegmentationValueObject(
        segmentationValueObject.children,
        searchValue,
        filterProperty,
      );
      if (foundSegmentationValueObject) {
        res = foundSegmentationValueObject;
        return true;
      }
    }
    return false;
  });

  return res;
}

/**
 * Tries to find the segmentation value object in the currently available blocks
 * @param  { object } segmentationList : list of blocks in a dict where the key is the segmentationId
 * @param  { string } segmentationId  : id of the segmentation block
 * @param  { string } searchValue     : the value that should match the filterproperty exactly
 * @param  { string } filterProperty  : the property of the segmentation value object that should match the searchvalue
 * @return { object }                 : the segmentation value object or null

 */
export function getSegmentationValue(
  segmentationList,
  segmentationId,
  searchValue,
  filterProperty = 'segmentationValueId',
) {
  const segmentation = segmentationList[segmentationId];
  const segmentationObjects =
    segmentation?.sortedSegmentationObjects ||
    segmentation?.segmentationObjects; // RI uses sortedSegmentationObjects, PI uses segmentationObjects

  if (segmentationObjects && searchValue) {
    return findSegmentationValueObject(
      segmentationObjects,
      searchValue,
      filterProperty,
    );
  }

  return null;
}

/**
 * Returns an array of segmentation objects that are parents to the given segmentation object.
 * @param  { object }  segmentationValueObj The value object you clicked on
 * @return { array }                        Array of parent segmentation objects (ordered by highest level parent to lowest level parent)
 */
export function getParents(segmentationValueObj) {
  const parentArray = [];

  // Make an array of the segmentation objects parents.
  let currentSegmentationValueObj = segmentationValueObj;
  while (currentSegmentationValueObj.parent) {
    parentArray.push(currentSegmentationValueObj.parent);
    currentSegmentationValueObj = currentSegmentationValueObj.parent;
  }

  // Reverse the array so the highest level parent is first and lowest level parent is last.
  return parentArray.reverse();
}

/**
 * Returns true if the segmentation item has any nested children in the filters.
 * @param  { object }  segmentationValueObj The value object you clicked on
 * @param  { object }  currentFilters       The current active filters
 * @return { boolean }                      Whether the segmentation item has active children
 */
export function hasActiveChildren(segmentationValueObj, currentFilters) {
  let hasChildrenInFilters = false;

  if (segmentationValueObj.children) {
    hasChildrenInFilters = segmentationValueObj.children.some((child) => {
      if (
        currentFilters[child.segmentationId] &&
        currentFilters[child.segmentationId].includes(child.segmentationValueId)
      ) {
        return true;
      }

      if (child.children) {
        // Find nested children recursively.
        return hasActiveChildren(child, currentFilters);
      }

      return false;
    });
  }

  return hasChildrenInFilters;
}

/**
 * Checks if a collection of value objects are all selected as filters
 * @param  { array }  segmentationValueObjects  : array containing value objects to check against
 * @param { object }  currentFilters         : current active filters
 * @return { boolean }                          : True if all are selected as a filter
 */
export function hasAllSelected(segmentationValueObjects, currentFilters) {
  // Check if every object is selected as filter
  return segmentationValueObjects.every((segmentationValueObject) => {
    return hasFilter(
      segmentationValueObject.segmentationId,
      segmentationValueObject.segmentationValueId,
      currentFilters,
    );
  });
}

/**
 * Removes a filter from the list of filters
 * @param  { string }  segmentationId      : id of the block
 * @param  { string }  segmentationValueId : id of the value
 * @param  { object }  currentFilters      : current active filters
 * @return { object }                      : updated filters
 */
export function removeFilter(
  segmentationId,
  segmentationValueId,
  currentFilters,
) {
  const index = currentFilters[segmentationId].indexOf(segmentationValueId);

  if (index > -1) {
    currentFilters[segmentationId].splice(index, 1);
  }

  if (currentFilters[segmentationId].length === 0) {
    delete currentFilters[segmentationId];
  }

  return currentFilters;
}

/**
 * Removes an array of filters from the list of filters
 * @param { Array } segmentationValueObjects : An array of value objects used within the app to remove from filters
 * @param { object } currentFilters          : current active filters
 */
export function removeFilters(segmentationValueObjects, currentFilters) {
  // No need to do anything if there are no filters at all, check length of filters
  if (Object.keys(currentFilters).length) {
    segmentationValueObjects.forEach((child) => {
      if (
        hasFilter(
          child.segmentationId,
          child.segmentationValueId,
          currentFilters,
        )
      ) {
        removeFilter(
          child.segmentationId,
          child.segmentationValueId,
          currentFilters,
        );
      }
    });
  }
}

/**
 * Toggles a filter on/off depending on its presence in the currentFilters
 * @param  { string }  segmentationId      : id of the block
 * @param  { string }  segmentationValueId : id of the value
 * @param  { object }  currentFilters      : current active filters
 */
export function toggleFilter(
  segmentationId,
  segmentationValueId,
  currentFilters,
) {
  // If the filter is not yet present, add it
  if (hasFilter(segmentationId, segmentationValueId, currentFilters)) {
    // If the filter is already present, remove it, and make sure that empty filter buckets are deleted
    removeFilter(segmentationId, segmentationValueId, currentFilters);
  } else {
    addFilter(segmentationId, segmentationValueId, currentFilters);
  }
}

/**
 * Merges all existing filter objects in a single object
 * @param  { string } segmentationFilters : current segmentation filters
 * @param  { string } stayDateFilter      : current stay date filter
 * @return { object } all filters in a single object
 */
export function mergeFilters(segmentationFilters, stayDateFilter) {
  const filters = parseObject(segmentationFilters);
  if (stayDateFilter) {
    filters[RI_BREAKDOWNS.IMPACT_DATE] =
      parseObject(stayDateFilter)[RI_BREAKDOWNS.IMPACT_DATE];
  }

  return filters;
}

/**
 * Adds filters to the activeFilters property
 * Used for the filter bar
 * If it is already in there, it will update the value
 * If it is not in there yet, it will add it to the end
 * If it is in there, but not in the passed filters, it will be removed
 * @param { array } activeFilters                     : current filters to be displayed in the filter bar
 * @param { object } newFilters                          : new filters to be added (can also be already present)
 * @param { function } segmentationNameFormatter      : formats top value in filter bar block
 * @param { function } segmentationValueFormatter     : formats bottom value in fitler bar block
 * @param { object } segmentationValueFormatterParams : extra params for the formatter
 * @return { array }                                  : the updated filters
 */
export function addFiltersToActiveFilters(
  activeFilters,
  newFilters,
  segmentationNameFormatter,
  segmentationValueFormatter,
  segmentationValueFormatterParams = {},
  segmentationNameFormatterParams = {},
) {
  // Delete updated keys, remove all remaining filters afterwards
  let activeFilterKeys = activeFilters.map((activeFilter) => activeFilter.key);

  let newActiveFilters = [...activeFilters];

  // First update existing entries
  newActiveFilters.forEach((activeFilter) => {
    if (newFilters[activeFilter.key]) {
      const values = [];
      newFilters[activeFilter.key].forEach((segmentationValueId) => {
        // Use the formatter to get the value
        const { segmentationDisplayNameOverride } =
          segmentationValueFormatterParams;
        const segmentationValueKey =
          segmentationDisplayNameOverride?.[activeFilter.key]?.[
            segmentationValueId
          ] ?? segmentationValueId;
        const displayValue = segmentationValueFormatter(
          activeFilter.key,
          segmentationValueKey,
          segmentationValueFormatterParams,
        );
        // Save key (segmentationValueId), displayValue
        values.push({
          key: segmentationValueId,
          displayValue,
        });
      });
      set(activeFilter, 'values', values);
      // Note: this needs to be done because the can change between view changes
      set(
        activeFilter,
        'translationKey',
        segmentationNameFormatter(
          activeFilter.key,
          segmentationNameFormatterParams,
        ),
      );
      // Delete from newFilters, so it does not get added twice
      delete newFilters[activeFilter.key];
      // Delete from activeFilterKeys, so we know which keys are still in use
      activeFilterKeys = activeFilterKeys.filter(
        (key) => key !== activeFilter.key,
      );
    }
  });

  // Add new filters
  Object.keys(newFilters).forEach((newFilterKey) => {
    const values = [];
    newFilters[newFilterKey].forEach((segmentationValueId) => {
      // Use the formatter to get the value
      const displayValue = segmentationValueFormatter(
        newFilterKey,
        segmentationValueId,
        segmentationValueFormatterParams,
      );
      // Save key (segmentationValueId), displayValue
      values.push({
        key: segmentationValueId,
        displayValue,
      });
    });
    newActiveFilters.push({
      key: newFilterKey,
      translationKey: segmentationNameFormatter(
        newFilterKey,
        segmentationNameFormatterParams,
      ),
      values,
    });
  });

  // Get all filters we should delete
  const filtersToDelete = newActiveFilters.filter(
    (filter) => activeFilterKeys.indexOf(filter.key) !== -1,
  );

  // Delete them if necessary
  if (filtersToDelete.length) {
    newActiveFilters = newActiveFilters.filter(
      (filter) => !filtersToDelete.includes(filter),
    );
  }

  newActiveFilters.reverse();

  return newActiveFilters;
}
